import { useState, useEffect } from 'react';
import { getPlanDisplayName, usePlanPromptLimits } from '../lib/plans';

interface DowngradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  currentPlan: string;
  targetPlan: string;
  currentBillingInterval?: 'monthly' | 'yearly' | null;
  targetBillingInterval?: 'monthly' | 'yearly' | null;
  loading?: boolean;
}

export function DowngradeModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  currentPlan, 
  targetPlan,
  currentBillingInterval = null,
  targetBillingInterval = null,
  loading = false 
}: DowngradeModalProps) {
  const planPromptLimits = usePlanPromptLimits();
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else if (isVisible) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isVisible]);

  if (!isVisible) return null;

  const currentPlanName = getPlanDisplayName(currentPlan);
  const targetPlanName = getPlanDisplayName(targetPlan);
  const targetPrompts = planPromptLimits[targetPlan] ?? 0;
  const isBillingSwitch = currentPlan === targetPlan && currentBillingInterval !== targetBillingInterval;

  return (
    <div className={`delete-modal ${isVisible ? 'is-visible' : ''} ${isAnimating ? 'is-open' : ''} ${!isAnimating && isVisible ? 'is-closing' : ''}`}>
      <div className="white-background">
        <div 
          className="modal-close"
          onClick={onClose}
        >
          <div className="rotated-text">+</div>
        </div>
        
        <div className="text-size-medium text-weight-semibold">
          {isBillingSwitch
            ? `Switch to ${targetBillingInterval}?`
            : `Downgrade to ${targetPlanName}?`}
        </div>
        
        <div className="spacer-tiny"></div>
        
        <div className="text-size-regular text-colour-alt-grey">
          {isBillingSwitch ? (
            <>
              {currentBillingInterval === 'yearly' && targetBillingInterval === 'monthly' ? (
                <>
                  Your {currentPlanName} yearly plan will continue until the end of your current billing period.
                  <br /><br />
                  Monthly billing will begin after that. No refund will be applied for the current yearly term.
                </>
              ) : (
                <>
                  We&apos;ll switch your billing from {currentBillingInterval} to {targetBillingInterval}.
                  <br /><br />
                  Stripe will calculate any prorated billing that applies to the remaining period.
                </>
              )}
            </>
          ) : targetPlan === 'free' ? (
            <>
              Your subscription will be cancelled at the end of your current billing period.
              You&apos;ll keep access to your {currentPlanName} plan features until then.
              <br /><br />
              After that, you will no longer be able to monitor prompts or access analytics.
            </>
          ) : (
            <>
              Your plan will be downgraded to {targetPlanName} at the end of your current billing period.
              You&apos;ll keep access to your {currentPlanName} plan features until then.
              <br /><br />
              Starting next billing cycle, you&apos;ll be charged the {targetPlanName} plan rate and can monitor up to {targetPrompts} prompts.
            </>
          )}
        </div>
        
        <div className="spacer-small"></div>
        
        <div className="modal-button-flex" style={{ display: 'flex', gap: '0.7rem' }}>
          <button 
            className="button is-small is-white has-inner-shadow"
            onClick={onClose}
            disabled={loading}
          >
            No, don't
          </button>
          <button 
            className="button is-small is-warning"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Confirm Change'}
          </button>
        </div>
      </div>
      
      <div className="close-layer" onClick={onClose}></div>
    </div>
  );
}
